import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Trash2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { RequireAuth } from "@/components/RequireAuth";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { buildAppMeta, SITE_URL } from "@/lib/seo";
import { getClipStorage, type ClipRow } from "@/lib/storage.functions";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/app/settings/storage")({
  head: () => ({
    meta: buildAppMeta({
      title: "Clip storage — CoachQuick",
      description: "Manage uploaded clips and free up storage on your CoachQuick account.",
      path: "/app/settings/storage",
    }),
    links: [{ rel: "canonical", href: `${SITE_URL}/app/settings/storage` }],
  }),
  component: () => (
    <RequireAuth role="coach">
      <StoragePage />
    </RequireAuth>
  ),
});

type Filter = "largest" | "oldest" | "expiring";

function StoragePage() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState<Filter>("largest");

  const { data, isLoading, refetch, isRefetching } = useQuery({
    queryKey: ["clip-storage"],
    queryFn: () => getClipStorage(),
    staleTime: 10_000,
  });

  const deleteOne = useMutation({
    mutationFn: async (clip: ClipRow) => {
      if (clip.storage_path) {
        await supabase.storage
          .from("session-clips")
          .remove([clip.storage_path])
          .catch(() => {});
      }
      if (clip.poster_path) {
        await supabase.storage
          .from("session-clips")
          .remove([clip.poster_path])
          .catch(() => {});
      }
      const { error } = await supabase
        .from("session_clips" as never)
        .delete()
        .eq("id", clip.id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Clip deleted");
      qc.invalidateQueries({ queryKey: ["clip-storage"] });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Failed"),
  });

  const deleteMany = useMutation({
    mutationFn: async (ids: string[]) => {
      const targets = (data?.clips ?? []).filter((c) => ids.includes(c.id));
      const objectPaths = targets
        .flatMap((c) => [c.storage_path, c.poster_path])
        .filter((p): p is string => !!p);
      if (objectPaths.length > 0) {
        await supabase.storage
          .from("session-clips")
          .remove(objectPaths)
          .catch(() => {});
      }
      const { error } = await supabase
        .from("session_clips" as never)
        .delete()
        .in("id", ids);
      if (error) throw error;
    },
    onSuccess: (_, ids) => {
      toast.success(`Deleted ${ids.length} clip${ids.length === 1 ? "" : "s"}`);
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ["clip-storage"] });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Failed"),
  });

  const sortedClips = sortClips(data?.clips ?? [], filter);
  const usedPct = data ? Math.min(100, Math.round((data.used_bytes / data.quota_bytes) * 100)) : 0;
  const allSelected = sortedClips.length > 0 && sortedClips.every((c) => selected.has(c.id));
  const totalClips = data?.clips.length ?? 0;

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(sortedClips.map((c) => c.id)));
  }

  return (
    <AppShell>
      <div className="-mx-4 -my-6 min-h-[calc(100vh-4rem)] bg-canvas-soft px-4 py-6 md:-mx-8 md:px-8">
        <div className="flex flex-col gap-5">
          {/* Breadcrumb + heading */}
          <div>
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/app/settings">Settings</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>Clip storage</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
            <h1 className="mt-2 font-display text-3xl font-black tracking-tight md:text-4xl">
              Clip storage
            </h1>
            <p className="mt-1.5 text-sm text-muted-foreground">
              Manage uploaded videos. Linked clips (YouTube/Vimeo) don't count toward your storage.
            </p>
          </div>

          {/* Storage-used hero */}
          <section className="rounded-[20px] bg-ink-panel px-7 py-6 text-white">
            <div className="flex flex-wrap items-end justify-between gap-6">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/60">
                  Storage used
                </p>
                <p className="mt-2.5 font-display text-[44px] font-black leading-none tracking-[-0.02em] [font-variant-numeric:tabular-nums]">
                  {data ? fmtBytes(data.used_bytes) : "—"}
                  <span className="ml-2 font-sans text-base font-normal text-white/60">
                    of {data ? fmtBytes(data.quota_bytes) : "—"}
                  </span>
                </p>
              </div>
              <div className="text-right text-sm text-white/70">
                <p>
                  {data?.upload_count ?? 0} uploaded {data?.upload_count === 1 ? "clip" : "clips"}
                </p>
                {data?.next_delete_in_days != null && (
                  <p className="mt-1">
                    Next auto-delete in{" "}
                    <strong className="font-semibold text-white">
                      {data.next_delete_in_days} {data.next_delete_in_days === 1 ? "day" : "days"}
                    </strong>
                  </p>
                )}
              </div>
            </div>
            <Progress value={usedPct} className="mt-4 bg-white/15" />
          </section>

          {/* 90-day warning */}
          <section className="flex items-start gap-2.5 rounded-[16px] border border-dashed bg-muted px-4 py-3.5 sm:px-[18px]">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <p className="text-sm text-muted-foreground [text-wrap:pretty]">
              <strong className="font-semibold text-foreground">
                Clips are deleted 90 days after upload.
              </strong>{" "}
              This keeps CoachQuick fast and affordable. The recap email already reminds your
              clients to download anything they want to keep.
            </p>
          </section>

          {/* Uploaded clips */}
          <section className="rounded-[20px] border bg-card p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="font-display text-lg font-extrabold tracking-[-0.02em]">
                Uploaded clips
              </h2>
              <div className="flex flex-wrap gap-1.5">
                <FilterChip active={filter === "largest"} onClick={() => setFilter("largest")}>
                  Largest
                </FilterChip>
                <FilterChip active={filter === "oldest"} onClick={() => setFilter("oldest")}>
                  Oldest
                </FilterChip>
                <FilterChip active={filter === "expiring"} onClick={() => setFilter("expiring")}>
                  Expiring this week
                </FilterChip>
              </div>
            </div>

            {selected.size > 0 && (
              <div className="mt-3.5 flex flex-wrap items-center justify-between gap-3 rounded-[12px] border bg-muted px-3.5 py-3 text-sm">
                <span>
                  {selected.size} selected ({fmtBytes(sumSelected(sortedClips, selected))})
                </span>
                <Button
                  variant="destructive"
                  size="sm"
                  disabled={deleteMany.isPending}
                  onClick={() => {
                    if (!confirm(`Delete ${selected.size} clip(s)? This can't be undone.`)) return;
                    deleteMany.mutate(Array.from(selected));
                  }}
                >
                  <Trash2 className="mr-1 h-4 w-4" /> Delete selected
                </Button>
              </div>
            )}

            {isLoading ? (
              <p className="mt-4 text-sm text-muted-foreground">Loading…</p>
            ) : sortedClips.length === 0 ? (
              <p className="mt-5 text-sm text-muted-foreground">
                {totalClips === 0
                  ? "No uploaded clips. When you upload a video to a lesson, it will show up here."
                  : "No clips expiring this week."}
              </p>
            ) : (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="w-8 pb-2.5 pr-2">
                        <Checkbox checked={allSelected} onCheckedChange={() => toggleAll()} />
                      </th>
                      <th className="pb-2.5 pr-3 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                        Caption
                      </th>
                      <th className="pb-2.5 pr-3 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                        Client
                      </th>
                      <th className="pb-2.5 pr-3 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                        Lesson
                      </th>
                      <th className="pb-2.5 pr-3 text-right text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                        Size
                      </th>
                      <th className="pb-2.5 pr-3 text-right text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                        Expires
                      </th>
                      <th className="pb-2.5"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedClips.map((c) => (
                      <tr key={c.id} className="border-b last:border-0">
                        <td className="py-3 pr-2 align-top">
                          <Checkbox
                            checked={selected.has(c.id)}
                            onCheckedChange={() => toggle(c.id)}
                          />
                        </td>
                        <td className="py-3 pr-3">
                          <p className="truncate font-medium">{c.caption || "Untitled clip"}</p>
                          <p className="text-xs text-muted-foreground [font-variant-numeric:tabular-nums]">
                            {c.duration_seconds ? fmtDuration(c.duration_seconds) : ""}
                          </p>
                        </td>
                        <td className="py-3 pr-3 text-muted-foreground">{c.client_name ?? "—"}</td>
                        <td className="py-3 pr-3 text-muted-foreground">
                          {c.session_date ? fmtDate(c.session_date) : "—"}
                        </td>
                        <td className="py-3 pr-3 text-right [font-variant-numeric:tabular-nums]">
                          {c.size_bytes ? fmtBytes(c.size_bytes) : "—"}
                        </td>
                        <td className="py-3 pr-3 text-right">
                          <ExpiryBadge days={c.days_until_delete} />
                        </td>
                        <td className="py-2 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label="Delete clip"
                            disabled={deleteOne.isPending}
                            onClick={() => {
                              if (!confirm("Delete this clip? This can't be undone.")) return;
                              deleteOne.mutate(c);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="mt-4 flex justify-end">
              <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isRefetching}>
                {isRefetching ? "Refreshing…" : "Refresh"}
              </Button>
            </div>
          </section>
        </div>
      </div>
    </AppShell>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
        active
          ? "border-primary bg-primary text-primary-foreground"
          : "border-border bg-background text-muted-foreground hover:text-foreground"
      }`}
    >
      {children}
    </button>
  );
}

function ExpiryBadge({ days }: { days: number }) {
  let cls = "bg-muted text-muted-foreground";
  if (days <= 3) cls = "bg-destructive/15 text-destructive";
  else if (days <= 14) cls = "bg-amber-500/15 text-amber-700 dark:text-amber-300";
  return (
    <span className={`inline-block rounded-lg px-2 py-0.5 text-xs font-medium tabular-nums ${cls}`}>
      {days === 0 ? "today" : `${days}d`}
    </span>
  );
}

function sortClips(clips: ClipRow[], filter: Filter): ClipRow[] {
  const arr = [...clips];
  if (filter === "largest") {
    arr.sort((a, b) => (b.size_bytes ?? 0) - (a.size_bytes ?? 0));
  } else if (filter === "oldest") {
    arr.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  } else {
    return arr
      .filter((c) => c.days_until_delete <= 7)
      .sort((a, b) => a.days_until_delete - b.days_until_delete);
  }
  return arr;
}

function sumSelected(clips: ClipRow[], ids: Set<string>): number {
  return clips.filter((c) => ids.has(c.id)).reduce((s, c) => s + (c.size_bytes ?? 0), 0);
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function fmtDuration(s: number): string {
  const m = Math.floor(s / 60);
  const r = Math.round(s % 60);
  return `${m}:${r.toString().padStart(2, "0")}`;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
